/**
 * Movement is limited to a single axis: a ring around the campfire in the middle
 * of the clearing. Participants slide along it in one direction or the other and
 * always face the fire, which is what the acoustics are built on.
 */

// The campfire in the background art sits slightly below the canvas center.
/**
 * The plane the ring sits in: the 960x640 coordinate space that travels over the
 * network and drives the acoustics. Not a screen size — the view is painted from
 * these coordinates, never in them.
 */
export const SCENE_CANVAS_WIDTH = 960;
export const SCENE_CANVAS_HEIGHT = 640;

export const RING_CENTER = { x: 484, y: 356 };
export const RING_RADIUS = 140;

export interface Point {
  x: number;
  y: number;
}

/** Point on the ring for a given angle (radians, screen coordinates). */
export const angleToPoint = (angle: number): Point => ({
  x: RING_CENTER.x + RING_RADIUS * Math.cos(angle),
  y: RING_CENTER.y + RING_RADIUS * Math.sin(angle),
});

/** Angle of the ring point closest to an arbitrary position. */
export const pointToAngle = (p: Point): number =>
  Math.atan2(p.y - RING_CENTER.y, p.x - RING_CENTER.x);

/** Snap any position onto the ring. */
export const snapToRing = (p: Point): Point => angleToPoint(pointToAngle(p));

/** Shortest signed angular distance from `from` to `to`, in (-PI, PI]. */
export const shortestArc = (from: number, to: number): number => {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
};

/** Top of the ring — where the first person to arrive stands. */
export const RING_START_ANGLE = -Math.PI / 2;

/** Evenly spaced spots on the ring, starting at the top. */
export const ringLayout = (count: number): Point[] =>
  Array.from({ length: count }, (_, i) =>
    angleToPoint(RING_START_ANGLE + (i * Math.PI * 2) / Math.max(1, count)),
  );

/**
 * Listener orientation: a listener looks at what they are gathered around, so
 * left/right is measured in their own rotated frame rather than on the screen
 * axes.
 *
 * `facing` is passed rather than assumed. It was `RING_CENTER` for as long as
 * there was one thing to face; a room can hold several centres at once, and
 * which one this listener is turned towards is the mode's to say (#667).
 */
export const orientedPan = (
  listener: Point,
  source: Point,
  panDistance: number,
  facing: Point,
): number => {
  const fx = facing.x - listener.x;
  const fy = facing.y - listener.y;
  const len = Math.hypot(fx, fy);
  // Standing exactly on the centre: fall back to the plain screen axis.
  const forward = len < 1 ? { x: 0, y: -1 } : { x: fx / len, y: fy / len };
  // Listener's right-hand direction (screen y grows downwards).
  const right = { x: -forward.y, y: forward.x };
  const rx = source.x - listener.x;
  const ry = source.y - listener.y;
  const projection = rx * right.x + ry * right.y;
  return Math.max(-1, Math.min(1, projection / panDistance));
};

/** Number of discrete standing spots around the campfire. */
export const SPOT_COUNT = 12;

/** Angle of a spot index (wraps around). */
export const spotAngle = (index: number): number =>
  RING_START_ANGLE +
  ((((index % SPOT_COUNT) + SPOT_COUNT) % SPOT_COUNT) * Math.PI * 2) /
    SPOT_COUNT;

/** World point of a spot index. */
export const spotPoint = (index: number): Point =>
  angleToPoint(spotAngle(index));

/** All spots, in index order. */
export const allSpots = (): Point[] =>
  Array.from({ length: SPOT_COUNT }, (_, i) => spotPoint(i));

/** Index of the spot closest to an arbitrary position. */
export const nearestSpot = (p: Point): number => {
  const angle = pointToAngle(p);
  const raw = Math.round(
    (shortestArc(RING_START_ANGLE, angle) / (Math.PI * 2)) * SPOT_COUNT,
  );
  return ((raw % SPOT_COUNT) + SPOT_COUNT) % SPOT_COUNT;
};

/**
 * Nearest free spot walking in `direction` (+1 = counter-clockwise on screen,
 * matching the left arrow). Returns null when the ring is fully occupied.
 */
export const nextFreeSpot = (
  from: number,
  direction: 1 | -1,
  occupied: Iterable<number>,
): number | null => {
  const taken = new Set(occupied);
  for (let step = 1; step <= SPOT_COUNT; step += 1) {
    const candidate =
      (((from + direction * step) % SPOT_COUNT) + SPOT_COUNT) % SPOT_COUNT;
    if (!taken.has(candidate)) return candidate;
  }
  return null;
};

/** Closest free spot to a point, searching outwards in both directions. */
export const nearestFreeSpot = (
  p: Point,
  occupied: Iterable<number>,
): number | null => {
  const taken = new Set(occupied);
  const start = nearestSpot(p);
  if (!taken.has(start)) return start;
  for (let step = 1; step <= SPOT_COUNT; step += 1) {
    const a = (((start + step) % SPOT_COUNT) + SPOT_COUNT) % SPOT_COUNT;
    if (!taken.has(a)) return a;
    const b = (((start - step) % SPOT_COUNT) + SPOT_COUNT) % SPOT_COUNT;
    if (!taken.has(b)) return b;
  }
  return null;
};

/**
 * Everyone appears on the movement axis — the ring around the campfire — at the
 * top of it, facing the fire.
 */
export const getRingSpawnPosition = (): Point => angleToPoint(RING_START_ANGLE);
